import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Paper, Stack, Switch, Title } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { useCreatePlaylist } from '~/api/mutations'
import { TextInput } from '~/components/primitives/form'

export const Route = createFileRoute('/_app/playlists/new')({
  component: NewPlaylistPage,
})

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  comment: z.string().optional(),
  public: z.boolean(),
})

type FormData = z.infer<typeof schema>

function NewPlaylistPage() {
  const navigate = useNavigate()
  const createPlaylist = useCreatePlaylist()

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', comment: '', public: false },
  })

  const onSubmit = async (data: FormData) => {
    try {
      await createPlaylist.mutateAsync(data)
      notifications.show({ title: 'Playlist created', message: data.name, color: 'green' })
      navigate({ to: '/playlists' })
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to create playlist', color: 'red' })
    }
  }

  return (
    <Stack gap="md">
      <Title order={2}>New Playlist</Title>
      <Paper withBorder p="lg" maw={500}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap="md">
            <TextInput
              name="name"
              control={control}
              label="Name"
              placeholder="My Playlist"
              required
            />
            <TextInput
              name="comment"
              control={control}
              label="Description"
              placeholder="Optional description"
            />
            <Controller
              name="public"
              control={control}
              render={({ field }) => (
                <Switch label="Public" checked={field.value} onChange={field.onChange} />
              )}
            />
            <Button type="submit" loading={isSubmitting}>
              Create
            </Button>
          </Stack>
        </form>
      </Paper>
    </Stack>
  )
}
